import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { CuratedOrganization, MayTerm } from "models";
import { db } from "services";
import { dbCollections } from "utilities";

/**
 * Retrieves a list of all May terms from (where?), using the map view type. Used in background loader in index.tsx
 * @returns A Promise containing the list of all May terms.
 */
export async function getAllMayTerms(): Promise<MayTerm[]> {
  const mayTermQuery = query(
    collection(db, dbCollections.memberInfo),
    where("type", "==", "may-term")
  );
  const snipshot = await getDocs(mayTermQuery);
  return snipshot.docs.map(doc => {
    return { ...doc.data(), id: doc.id } as MayTerm;
  });
}

/**
 * Retrieves more detailed data for one specific May term on (where?).
 * @param username The username of a specific May term.
 * @returns A more detailed blob of data for a specific May term on (not peoplegrove).
 */
export async function getMayTerm(id: string): Promise<MayTerm | undefined> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists() ? ({ ...snapshot.data(), id: snapshot.id } as MayTerm) : undefined;
}

/**
 * Retrieves all curated alumni from the database.
 * @returns A Promise containing the retrieved documents as CuratedAlum objects.
 */
export async function getMayTermInfo(): Promise<CuratedOrganization[]> {
  const mayTermQuery = query(
    collection(db, dbCollections.memberInfo),
    where("type", "==", "may-term")
  );
  const snapshot = await getDocs(mayTermQuery);
  return snapshot.docs.map(doc => {
    return { ...doc.data(), id: doc.id } as CuratedOrganization;
  });
}

export async function getSpecificMayTerm(id: string): Promise<CuratedOrganization | undefined> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists()
    ? ({ ...snapshot.data(), id: snapshot.id } as CuratedOrganization)
    : undefined;
}

/**
 * Retrieves a specific may term from the database as a CuratedAlum.
 * @param id The ID of the may term to retrieve.
 * @returns A Promise containing a specific may term, or undefined if the ID doesn't exist.
 */
export async function getMayTermStory(id: string): Promise<CuratedOrganization | undefined> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists()
    ? ({ ...snapshot.data(), id: snapshot.id } as CuratedOrganization)
    : undefined;
}

/**
 * Updates an existing may term in the database. Some or all fields may
 * be updated, depending on what's included in the newAlum param.
 * @param id The ID of the may term to update.
 * @param newMayTerm The updated version of the may term.
 * @returns A Promise representing the status of the update.
 */
export async function updateMayTermStory(
  id: string,
  newMayTerm: CuratedOrganization,
): Promise<void> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  return updateDoc(docRef, { ...newMayTerm });
}

/**
 * Adds a new may term to the database and returns the id of the created document.
 * @param newMayTerm The may term to add.
 * @returns A Promise containing the ID of the added may term.
 */
export async function addMayTermStory(newMayTerm: CuratedOrganization): Promise<string> {
  const collectionRef = collection(db, dbCollections.memberInfo);
  const snapshot = await addDoc(collectionRef, newMayTerm);
  return snapshot.id;
}

/**
 * Deletes an item from the database.
 * @param id The ID of the item to delete.
 * @returns A Promise representing the status of the delete.
 */
export async function deleteMayStory(id: string): Promise<void> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  return deleteDoc(docRef);
}
